import { Grid, Link } from "@mui/material";
import {
  ApartmentRounded,
  BusinessCenterRounded,
  Facebook,
  LinkedIn,
  PaymentsRounded,
  Room,
  Twitter,
  WhatsApp,
} from "@mui/icons-material";
import XIcon from "@mui/icons-material/X";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import styles from "../../styles/pages/[jobId].module.scss";
import Meta from "../../layouts/Meta";
import StaticContainer from "../../layouts/StaticContainer";
import BlogPosts from "../../components/_shared/BlogPosts";
import { getCmsJobs, getJobPageData } from "../../services/cms/careers";
import { CareersFooter } from "../../services/cms/careersFooter";
import JobFooterInfo from "../../components/careers/JobFooterInfo";
import { Blog, Job } from "../../interfaces";
import ShareThisPost from "../../components/_shared/ShareThisPost";
import { Skeleton } from "@mui/material";
import { getBlogList } from "../../services/blogs";
import GeneralForm from "../../components/_shared/GeneralForm";
import CustomButton from "../../components/_shared/CustomButton";
import { safeUrl } from "../../utils/security";
import { GetStaticPaths, GetStaticProps } from "next";
import { MdKeyboardBackspace } from "react-icons/md";
import { cmsPageProps } from "../../lib/pageProps";
import ClosedJobPanel from "../../components/careers/ClosedJobPanel";

const JobDetails = (props: Props) => {
  const { job, careersFooter } = props;
  const router = useRouter();
  const { jobId }: any = router.query;

  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [socialButtons, setSocialButtons] = useState(null);
  const isClosed = job?.status === "closed";

  useEffect(() => {
    const call = async () => {
      if (!job) return router.replace("/404", router.asPath);

      const domain = window.location.hostname;
      const social_buttons = [
        {
          name: "job-facebook",
          icon: <Facebook />,
          color: "#4267B2",
          href: `https://www.facebook.com/sharer.php?u=https://${domain}${router.asPath}&t=${job.jobTitle}`,
        },
        {
          name: "job-twitter",
          icon: <XIcon />,
          color: "#1DA1F2",
          href: `https://twitter.com/share?url=https://${domain}${router.asPath}&text=${job.jobTitle}`,
        },
        {
          name: "job-linkedin",
          icon: <LinkedIn />,
          color: "#0072b1",
          href: `https://www.linkedin.com/shareArticle?mini=true&url=https://${domain}${router.asPath}&title=${job.jobTitle}&summary=&source=SOLS%2024%2F7`,
        },
        {
          name: "job-whatsapp",
          icon: <WhatsApp />,
          color: "#128C7E",
          href: `https://api.whatsapp.com/send?text=${job.jobTitle}%0Ahttps://${domain}${router.asPath}`,
        },
      ];
      setSocialButtons(social_buttons);

      // feed blogs array
      const blogs_json = await getBlogList();
      let blogs_array = [];
      blogs_json?.forEach((blog) => {
        blogs_array.push(blog.val());
      });
      blogs_array = blogs_array.reverse();
      setBlogs(blogs_array);
    };

    call();
  }, [job, jobId, router]);

  return (
    <StaticContainer title={job?.jobTitle}>
      <Meta
        title={job?.jobTitle}
        description={job?.jobMeta}
        keywords={job?.jobTitle}
        image={job?.jobImage}
        noindex={isClosed}
      />
      <Grid container spacing={5} sx={{ marginTop: "0" }}>
        {job ? (
          <Grid item xs={12} lg={9} className={styles.main}>
            <div className={styles.gridContainer}>
              <Link href={"/" + job.pageName} className={styles.backLink}>
                <MdKeyboardBackspace className={styles.backLinkIcon} />
                {job.jobType} Positions
              </Link>
            </div>

            <main className={styles.mainDetails}>
              <section>
                <div className={styles.description}>
                  <div className={styles.descCol}>
                    <span>
                      <BusinessCenterRounded
                        color="primary"
                        className={styles.icon}
                      />{" "}
                      {job.jobType}
                    </span>
                    <span>
                      <Room color="primary" className={styles.icon} />{" "}
                      {job.jobLocation}
                    </span>
                  </div>
                  {(job.department || job.salary) && (
                    <div className={styles.descCol}>
                      {job.department && (
                        <span>
                          <ApartmentRounded
                            color="primary"
                            className={styles.icon}
                          />{" "}
                          {job.department}
                        </span>
                      )}
                      {job.salary && (
                        <span>
                          <PaymentsRounded
                            color="primary"
                            className={styles.icon}
                          />{" "}
                          {job.salary}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </section>

              {!isClosed && (
                <div className={styles.applyNowBtn}>
                  <a
                    href={job.portalId == 0 ? safeUrl(job.formId) : "#job-hubspot-form"}
                    target={job.portalId == 0 ? "_blank" : "_self"}
                  >
                    <CustomButton>Apply Now</CustomButton>
                  </a>
                </div>
              )}
            </main>

            <div
              className={styles.descHtml}
              dangerouslySetInnerHTML={{ __html: job.jobDescHtml }}
            />

            {isClosed ? (
              <div id="job-status" className={styles.form}>
                <ClosedJobPanel departmentId={job.departmentId} />
              </div>
            ) : job.portalId == 0 ? (
              <div className={styles.applyNowBtn}>
                <a href={safeUrl(job.formId)} target="_blank">
                  <CustomButton fullWidth>Apply Now</CustomButton>
                </a>
              </div>
            ) : (
              <div id="job-hubspot-form" className={styles.form}>
                <GeneralForm
                  title="Interested in this position?"
                  portalId={job.portalId ? job.portalId?.toString() : ""}
                  formId={job.formId}
                />
              </div>
            )}

            <ShareThisPost socialButtons={socialButtons} />

            {careersFooter && <JobFooterInfo footer={careersFooter} />}
          </Grid>
        ) : (
          <Grid item xs={12} lg={9} className={styles.main}>
            <main className={styles.mainDetails}>
              <Skeleton
                className={styles.thumbnail}
                variant="rectangular"
                width={75}
                height={75}
              />
            </main>

            <div className={styles.descHtml}>
              <Skeleton variant="text" width="95%" height={20} />
              <Skeleton variant="text" width="90%" height={16} />
              <Skeleton variant="text" width="95%" height={16} />
              <Skeleton variant="text" width="70%" height={16} />
            </div>

            <hr />
          </Grid>
        )}

        <Grid item xs={12} lg={3} style={{ paddingTop: "10px" }}>
          <BlogPosts blogs={blogs.slice(0, 6)} />
        </Grid>
      </Grid>
    </StaticContainer>
  );
};

// Both revalidate windows below are 60s, matching every other CMS-fed page on
// this site. getJobPageData reads the whole jobs, departments and subsidiaries
// collections on every regeneration, and with fallback: "blocking" an unknown
// slug runs this getStaticProps too — so a one-second window let a crawler walking made-up slugs
// re-trigger that read on every hit, the negative cache expiring before it could
// absorb any of them. The cost is that a newly published job can 404 for up to a
// minute; the CMS revalidation webhook is what makes a publish visible at once.
export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const { jobId }: any = params;
  const { job, careersFooter } = await getJobPageData(jobId);

  // A closed job keeps its page, marked closed. A draft, a missing slug or a
  // job outside this site's scope must 404: they are not in getStaticPaths, so
  // without this their URLs would answer 200 with an empty page.
  if (!job) {
    return { notFound: true, revalidate: 60 };
  }

  return {
    props: { job, careersFooter, ...(await cmsPageProps(locale, ["careers"])) },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const [careerJobs, internshipJobs] = await Promise.all([
    getCmsJobs("careers"),
    getCmsJobs("internship"),
  ]);
  const jobs: Job[] = [...careerJobs, ...internshipJobs];
  let paths: any[] = [];

  locales?.forEach((locale) => {
    jobs?.forEach((job) =>
      paths.push({ params: { jobId: job.jobId }, locale })
    );
  });

  // "blocking", not true: fallback: true answers an unknown slug with a 200
  // shell before getStaticProps runs, so notFound would only apply on the
  // second request.
  return { paths, fallback: "blocking" };
};

interface Props {
  job: Job;
  careersFooter?: CareersFooter;
}

export default JobDetails;
